type RecordingResult = {
  blob: Blob;
  durationMs: number;
};

type AudioLevelListener = (level: number) => void;

type PcmRecordingController = {
  stop: () => Promise<RecordingResult>;
  cancel: () => Promise<void>;
};

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

function createAudioContext() {
  const AudioContextCtor = window.AudioContext ?? window.webkitAudioContext;
  if (!AudioContextCtor) {
    throw new Error('当前浏览器不支持 Web Audio 录音');
  }
  return new AudioContextCtor();
}

function mergeBuffers(chunks: Float32Array[]) {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const result = new Float32Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

function encodeWav(samples: Float32Array, sampleRate: number) {
  const bytesPerSample = 2;
  const numChannels = 1;
  const dataLength = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);

  const writeString = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
  view.setUint16(32, numChannels * bytesPerSample, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeString(36, 'data');
  view.setUint32(40, dataLength, true);

  let offset = 44;
  for (let index = 0; index < samples.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, samples[index]));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
    offset += bytesPerSample;
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

export async function startPcmAudioRecording(onLevel?: AudioLevelListener): Promise<PcmRecordingController> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('当前浏览器或访问方式不支持麦克风录音，请使用 localhost、HTTPS 或支持媒体权限的浏览器。');
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const audioContext = createAudioContext();
  if (audioContext.state === 'suspended') {
    await audioContext.resume();
  }
  const source = audioContext.createMediaStreamSource(stream);
  const processor = audioContext.createScriptProcessor(4096, 1, 1);
  const silentGain = audioContext.createGain();
  const chunks: Float32Array[] = [];
  const startedAt = Date.now();
  let closed = false;

  silentGain.gain.value = 0;

  processor.onaudioprocess = (event) => {
    const input = event.inputBuffer.getChannelData(0);
    chunks.push(new Float32Array(input));
    if (onLevel) {
      let sum = 0;
      for (let i = 0; i < input.length; i += 1) {
        sum += input[i] * input[i];
      }
      const rms = Math.sqrt(sum / input.length);
      const normalized = Math.max(0, Math.min(1, rms * 3.2));
      onLevel(normalized);
    }
  };

  source.connect(processor);
  processor.connect(silentGain);
  silentGain.connect(audioContext.destination);

  const cleanup = async () => {
    if (closed) {
      return;
    }
    closed = true;
    processor.onaudioprocess = null;
    source.disconnect();
    processor.disconnect();
    silentGain.disconnect();
    stream.getTracks().forEach((track) => track.stop());
    await audioContext.close();
  };

  return {
    stop: async () => {
      const sampleRate = audioContext.sampleRate;
      const merged = mergeBuffers(chunks);
      if (merged.length === 0) {
        await cleanup();
        throw new Error('未采集到任何音频数据');
      }
      const blob = encodeWav(merged, sampleRate);
      const durationMs = Date.now() - startedAt;
      await cleanup();
      return { blob, durationMs };
    },
    cancel: async () => {
      await cleanup();
    },
  };
}
